<template>
  <el-card class="box-card">
    <div
        id="device-general"
        slot="header"
        class="clearfix">
      <span class="header-item">{{ $t('device.status_reports') }}</span>
      <span class="ring-card__header-buttons">
        <RefreshButton @click.native="getStatusReports" />
      </span>
    </div>
    <div
        v-loading="loading"
        class="card-content">
      <h4>CHARTS</h4>
      <StatusReportCharts
          :reports="reportData"
          :family-reports="data.reports"
          :device-health="data.deviceHealth"
          :use-device-health-fallback="pagination.currentPage === 1" />
      <h4>DETAILS</h4>
      <el-table
          v-if="!error.value"
          ref="statusReportsTable"
          style="width: 100%"
          :data="reportData"
          max-height="500"
          @cell-click="toggleRowExpansion">
        <el-table-column type="expand">
          <template slot-scope="scope">
            <el-row :gutter="20">
              <el-col :span="12">
                <h5 class="status-report-label">
                  FW Versions:
                </h5>
                <div v-if="isEmpty(scope.row.data.firmware_versions)">
                  <p>No data</p>
                </div>
                <div v-else>
                  <el-row
                      v-for="(value, key) in scope.row.data.firmware_versions"
                      :key="key"
                      :gutter="10">
                    <el-col :span="12">
                      <b>{{ key | capitalize }}</b>
                    </el-col>
                    <el-col :span="12">
                      {{ value }}
                    </el-col>
                  </el-row>
                </div>
              </el-col>
              <el-col :span="12">
                <h5 class="status-report-label">
                  Settings:
                </h5>
                <div v-if="isEmpty(scope.row.data.current_settings)">
                  <p>No data</p>
                </div>
                <div v-else>
                  <el-row
                      v-for="(value, key) in scope.row.data.current_settings"
                      :key="key"
                      :gutter="10">
                    <el-col :span="12">
                      <b>{{ key | capitalize }}</b>
                    </el-col>
                    <el-col :span="12">
                      {{ value }}
                    </el-col>
                  </el-row>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="12">
                <h5 class="status-report-label">
                  Statistics:
                </h5>
                <div v-if="isEmpty(scope.row.data.statistics)">
                  <p>No data</p>
                </div>
                <div v-else>
                  <el-row
                      v-for="(value, key) in scope.row.data.statistics"
                      :key="key"
                      :gutter="10">
                    <el-col :span="12">
                      <b>{{ key | capitalize }}</b>
                    </el-col>
                    <el-col :span="12">
                      {{ value }}
                    </el-col>
                  </el-row>
                </div>
              </el-col>
              <el-col :span="12">
                <h5 class="status-report-label">
                  Status:
                </h5>
                <div v-if="isEmpty(scope.row.data.status)">
                  <p>No data</p>
                </div>
                <div v-else>
                  <el-row
                      v-for="(value, key) in scope.row.data.status"
                      :key="key"
                      :gutter="10">
                    <el-col :span="12">
                      <b>{{ key | capitalize }}</b>
                    </el-col>
                    <el-col :span="12">
                      {{ value }}
                    </el-col>
                  </el-row>
                </div>
              </el-col>
            </el-row>
          </template>
        </el-table-column>
        <el-table-column
            label="Date"
            :render-header="dateFilterHeader">
          <template slot-scope="scope">
            <time-zone-formatter :current-time=" scope.row.timestamp" />
          </template>
        </el-table-column>
        <el-table-column
            label="Context"
            prop="data.context"
            :filters="tagFilters"
            :filter-method="filterTag">
          <template slot-scope="scope">
            {{ eventContextByType(scope.row.type, scope.row.data.context) }}
          </template>
        </el-table-column>
        <el-table-column
            label="Firmware Version">
          <template slot-scope="scope">
            app {{ get(scope.row.data, 'firmware_versions.app', 'Unknown') }}
          </template>
        </el-table-column>
        <el-table-column
            label="Summary">
          <template slot-scope="scope">
            <SummaryReportPopover :report="scope.row" />
            <span v-if="scope.row['is_dpd']">
              <font-awesome-icon
                  v-if="deviceIsPlugged(scope.row)"
                  icon="plug" />
              <font-awesome-icon
                  v-else
                  icon="battery-three-quarters" />
            </span>
            <!--  else if is_lpd -->
            <font-awesome-icon
                v-else
                icon="plug" />
          </template>
        </el-table-column>
      </el-table>

      <div class="status-reports-table__footer-pagination">
        <el-alert
            v-if="error.value"
            style="margin-bottom: 14px"
            :title="error.message"
            type="info"
            center
            show-icon />
        <el-pagination
            :small="true"
            :current-page.sync="pagination.currentPage"
            :page-sizes="[25, 50, 100, 200]"
            :page-size="pagination.limit"
            layout="sizes, prev, pager, next"
            :total="total"
            @size-change="onPageSizeChange"
            @current-change="onPageChange" />
      </div>
    </div>
  </el-card>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import RefreshButton from "@/containers/Customer/RefreshButton";
import {
  startCase,
  isEmpty,
  uniq,
  get,
  has,
  set,
  cloneDeep,
  isNaN,
  compact,
  map,
} from "lodash";
import DateRangeFilterPopover from "@/components/Shared/DateRangeFilterPopover";
import StatusReportCharts from "./Charts/StatusReportCharts";
import SummaryReportPopover from "./SummaryReportPopover";
import {
  loadContextMap,
  contextLabels,
} from "@/modules/shared/devices/statusReportContexts";

const { mapActions: deviceActions } = createNamespacedHelpers("db/device");

const tempConverter = {
  kelvinToFahrenheit(k) {
    return k * 1.8 - 459.67;
  },
  kelvinToCelsius(k) {
    return k - 273.15;
  },
  celciusToFahrenheit(c) {
    return c * 1.8 + 32;
  },
  round(value) {
    return Math.round(value * 10) / 10;
  },
};

export default {
  name: "DeviceStatusReports",
  components: {
    DateRangeFilterPopover,
    SummaryReportPopover,
    StatusReportCharts,
    RefreshButton,
  },
  filters: {},
  props: {
    device: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: null,
      dateFilter: [],
      error: {
        value: false,
        message: "Failed to load status report",
      },
      data: { reports: [], meta: {}, deviceHealth: {} },
      // Device type -> DDF status_report_context_map (null when unavailable).
      contextMaps: {},
      pagination: {
        limit: 25,
        timestamps: [],
        currentPage: 1,
      },
    };
  },
  computed: {
    doorbotId() {
      return this.device.doorbot_id;
    },
    // Device type -> { code: label }, rebuilt when a context map arrives.
    labelsByType() {
      const types = uniq(compact(map(this.data.reports, "type")));
      const labels = {};
      types.forEach((type) => {
        labels[type] = contextLabels(type, this.contextMaps[type]);
      });
      return labels;
    },
    tagFilters() {
      const type = get(this.data.reports, "[0].type");
      const labels = this.labelsByType[type] || {};
      return Object.keys(labels)
        .sort((a, b) => Number(a) - Number(b))
        .map((code) => ({ value: code, text: labels[code] }));
    },
    reportData() {
      if (isEmpty(this.dateFilter)) return this.data.reports;
      // date range
      let [startDt, endDt] = this.dateFilter;
      startDt = startDt.getTime();
      endDt = endDt.getTime();
      // filter reports in between date range
      return this.data.reports.filter((row) => {
        let rowDt = new Date(row.timestamp);
        rowDt = rowDt.getTime();
        return rowDt <= endDt && rowDt >= startDt;
      });
    },
    total() {
      const totalTimestamps = this.pagination.timestamps.length;
      const pageSize = this.pagination.limit;
      if (totalTimestamps === 0) return pageSize;
      return pageSize * totalTimestamps + pageSize;
    },
  },
  watch: {
    doorbotId: function (id, previousId) {
      this.getStatusReports();
    },
  },
  created() {
    this.getStatusReports();
  },
  methods: {
    formatStatusReport(reports) {
      let reportsCopy = cloneDeep(reports);
      return reportsCopy.map((report) => {
        try {
          let temp = get(report, "data.status.temp", null);
          let tempFormatted = this.temperatureFormatter(temp);
          set(report, "data.status.temp", tempFormatted);
          return report;
        } catch (e) {
          console.error(e);
        }
      });
    },
    temperatureFormatter(value) {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return " - ";
      }
      value = String(value);
      if (value && value.indexOf("F") === -1 && value.indexOf("C") === -1) {
        let temp = parseFloat(value);
        if (!isNaN(temp)) {
          let fahrenheit, celsius;
          if (temp < 150) {
            // is celsius temperature
            celsius = temp;
            fahrenheit = tempConverter.celciusToFahrenheit(celsius);
          } else {
            const kelvin = String(temp).length === 5 ? temp / 100 : temp / 10;
            celsius = tempConverter.kelvinToCelsius(kelvin);
            fahrenheit = tempConverter.kelvinToFahrenheit(kelvin);
          }
          return (
            "" +
            tempConverter.round(fahrenheit) +
            " F (" +
            tempConverter.round(celsius) +
            " C)"
          );
        }
      }
      return value;
    },
    ...deviceActions(["loadStatusReports"]),
    get,
    deviceIsPlugged(report) {
      return get(report, "admin_panel.battery.connected?", false);
    },
    eventContextByType(type, context) {
      const code = String(context);
      const labels =
        this.labelsByType[type] || contextLabels(type, this.contextMaps[type]);
      return labels[code] || `Unknown (context ${code})`;
    },
    loadContextMaps(reports) {
      const types = uniq(compact(map(reports, "type"))).filter(
        (type) => !has(this.contextMaps, type)
      );
      return Promise.all(
        types.map((type) =>
          loadContextMap(type).then((contextMap) => {
            this.$set(this.contextMaps, type, contextMap);
          })
        )
      );
    },
    filterTag(value, row) {
      return row.data.context == value;
    },
    onPageSizeChange(sizePage) {
      this.pagination.limit = sizePage;
      this.updateTimestamps({ clean: true });
      this.getStatusReports();
    },
    onPageChange() {
      this.getStatusReports();
    },
    lastTimestamp() {
      const currentPageZeroIndex = this.pagination.currentPage - 1;
      return this.pagination.timestamps[currentPageZeroIndex - 1];
    },
    getStatusReports() {
      this.loading = true;
      this.error.value = false;
      let doorbotId = this.device.doorbot_id;
      this.loadStatusReports({
        doorbotId,
        limit: this.pagination.limit,
        lastTimestamp: this.lastTimestamp(),
      })
        .then((response) => {
          this.data.deviceHealth = response.data.device_health || {};
          this.data.reports = this.formatStatusReport(response.data.reports);
          this.data.meta = response.data.meta;
          this.updateTimestamps({
            last_timestamp: this.data.meta.last_timestamp,
          });
          // Labels resolve once the type's map arrives; a failed or missing
          // map leaves the manual overrides and "Unknown (context N)".
          return this.loadContextMaps(this.data.reports);
        })
        .catch((e) => {
          this.error.value = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isEmpty(obj) {
      let name = Object.prototype.toString
        .call(obj)
        .match(/^\[object\s(.*)\]$/)[1];

      switch (name) {
        case "Object":
          return Object.keys(obj).length < 1;
        case "String":
          return true;
        case "Array":
          return obj.length < 1;
        default:
          return true;
      }
    },
    dateFilterHeader(h) {
      const self = this;
      return h(
        DateRangeFilterPopover,
        {
          props: {
            value: self.dateFilter,
          },
          on: {
            input(value) {
              self.dateFilter = value;
            },
          },
        },
        this.$slots.default
      );
    },
    updateTimestamps({ clean = false, last_timestamp }) {
      if (clean) {
        this.pagination.timestamps = [];
        return;
      }
      this.pagination.timestamps.push(last_timestamp);
      this.pagination.timestamps = uniq(this.pagination.timestamps);
    },
    toggleRowExpansion(row, column) {
      this.$refs.statusReportsTable.toggleRowExpansion(row);
    },
  },
};
</script>

<style lang="scss" scoped>
.fav {
  color: #e6a23c !important;
}
.not-fav {
  color: #999 !important;
}

.el-table .el-table__expanded-cell {
  padding: 5px;
  &:hover {
    background: #fff !important;
  }
  .status-report-label {
    background: #fafbfc;
    color: $--color-dusty-gray;
    font-weight: bold;
    padding: 5px;
    font-size: 15px;
  }
}

.curious-blue {
  color: $--color-curious-blue;
}

.status-reports-table__footer-pagination {
  margin: 40px 0 20px;
}

.card-content {
  .sr_summary-icon {
    color: $--color-curious-blue;
  }
  & > h2,
  h3,
  h4 {
    margin: 30px auto;
    color: $--color-dusty-gray;
    background: #fafbfc;
    font-weight: bold;
    padding: 5px;
    /*font-size: 15px;*/
  }
}

$good-indicator-color: #009f29;
$alert-indicator-color: #9f6000;
$needs_attention-indicator-color: #d8000c;
$color-opacity: opacity(0.9);

.status-report-summary-popover {
  padding: 10px;

  label {
    font-size: $--font-size-small;
    font-variant: all-small-caps;
    font-weight: 600;
  }

  .status-field-value {
    display: block;
    float: right;
  }

  .indicator-good {
    background-color: #e6f5ea;
    color: $good-indicator-color;
    font-size: small;
  }

  .indicator-alert {
    color: $alert-indicator-color;
    background-color: #feefb3;
    font-size: small;
  }

  .indicator-needs_attention {
    color: $needs_attention-indicator-color;
    background-color: #ffd2d2;
    font-size: small;
  }
}
</style>
